import { MemberListService } from 'src/app/core/service/grid-data/grid-data-member.service';
import { FHIR_DEFAULT_PAGE_URL, FhirService } from '../../core/service/fhir.service';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { State } from '@progress/kendo-data-query';
import { SettingService } from 'src/app/core/service/setting.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UserService } from 'src/app/core/service/user.service';
import { Subscription } from 'rxjs';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { ReferralComponent } from 'src/app/referral/referral.component';
import { MessageService } from 'src/app/core/service/message.service';
import { SelectEvent } from '@progress/kendo-angular-layout/tabstrip/events';
import { Router } from '@angular/router';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';

@Component({
  selector: 'app-fhir-summary',
  templateUrl: './fhir-summary.component.html',
  styleUrls: ['./fhir-summary.component.scss']
})
export class FhirSummaryComponent implements OnInit, OnDestroy
{
  @ViewChild('referral') public referral: ReferralComponent;

  public loaded = false;
  public member = null;
  public patientContext = null;
  public patientLabel = {};
  public referralsEnabled = false;
  public communityEnabled = false;
  public referralsCollapsed = false;
  public ehrShowAssessments = false;
  public multiplePatientsFound = false;
  public accessMessages = false;
  public unreadMessages = 0;
  public messageType = 'inbox';
  public selectedTab = 0;
  public subjectIdentifierMPI = null;

  private messagingSubscription: Subscription;

  constructor(
    private dictionaryService: DictionaryService,
    private router: Router,
    private fhirService: FhirService,
    private settingService: SettingService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private messengerService: MessengerService,
    private messageService: MessageService,
    private messageHandlerService: MessageHandlerService,
    private changeDetectorRef: ChangeDetectorRef,
    private memberListService: MemberListService)
  {
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'getCaseDetailsData')
        {
          this.refreshCases(2500);
        }
        else if (message && message.type && message.type === 'wsMessagesCount')
        {
          const responseMessage = JSON.parse(message.data.body);
          this.unreadMessages = responseMessage.unreadCount;
          this.changeDetectorRef.detectChanges();
        }
      }
    });
  }

  public get patientMPI(): string
  {
    return this.patientContext && (this.patientContext?.mpi ? this.patientContext?.mpi : null);
  }

  public get patientName(): string
  {
    return this.patientContext && (this.patientContext?.firstName || this.patientContext?.lastName) ?
      `${this.patientContext?.firstName} ${this.patientContext?.lastName}` : '';
  }

  public get patientDOB(): string
  {
    return this.patientContext ? this.patientContext?.dob : null;
  }

  /**
   * Provide loading property indicators for list.
   */
  public get loadingSubjects(): boolean
  {
    return this.memberListService.loading;
  }

  public ngOnInit(): void
  {
    this.dictionaryService.getCachedIdentifiers().subscribe({
      next: (data) =>
      {
        this.subjectIdentifierMPI = data.find(x => x.code.toLowerCase() === 'mpi');
        this.settingService.getCachedSessionSettings().subscribe({
          next: (sessionSetting) =>
          {
            this.ehrShowAssessments = sessionSetting.ehrShowAssessments;
            this.communityEnabled = sessionSetting.communityEnabled;
            this.patientLabel = { patientLabel: sessionSetting.patientLabel };
            this.patientContext = this.fhirService.patientContext;
            this.referralsEnabled = sessionSetting.communityEnabled && ((sessionSetting.referralsRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.referralsRoles) : false);
            this.accessMessages = (sessionSetting.messagesRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.messagesRoles) : false;

            this.loadPatient();
          }
        });
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public referralsOpenStateChanged(e: boolean): void
  {
    this.referralsCollapsed = e;
  }

  public newReferral(): void
  {
    this.referral.newReferral(this.ehrShowAssessments);
  }

  public backToSummary(): void
  {
    const patientId = this.fhirService.patientIdContext;
    this.fhirService.clearStorage(true);
    this.router.navigateByUrl(`/patient/${patientId}`);
  }

  /**
   * Get count of unread messages for the current user.
   */
  public getCountMessages(): void
  {
    if (this.accessMessages)
    {
      const unread = 0;
      if (this.authenticationService.isAuthenticated)
      {
        const user = this.authenticationService.currentUser;
        if (user.role !== 'ROLE_COMMUNITY_ADMIN' && user.role !== 'ROLE_SYSTEM_OPERATOR')
        {
          this.messageService.getCountMessageByUserAndStatus(this.authenticationService.currentUser.userName, unread).subscribe({
            next: (data) =>
            {
              this.unreadMessages = data;
            }
          });
        }
      }
    }
  }

  public onTabSelect(e: SelectEvent): void
  {
    this.selectedTab = e.index;
    if (this.selectedTab === 1)
    {
      this.messageType = 'inbox';
      this.router.navigateByUrl(`${FHIR_DEFAULT_PAGE_URL}/messages/${this.messageType}`);
    }
  }

  private loadPatient(): void
  {
    this.messageType = 'inbox';
    this.multiplePatientsFound = false;

    const state: State = { skip: 0, take: 5, filter: { logic: 'and', filters: [] } };

    if (this.patientMPI)
    {
      if (!this.subjectIdentifierMPI) 
      {
        this.messageHandlerService.displayError('MPI-DICTIONARY-NOT-FOUND');
        this.loaded = true;
        return;
      }
      else
      {
        state.filter.filters.push({ field: 'identifierDictionary', operator: 'eq', value: this.subjectIdentifierMPI.id, logic: 'and' });
        state.filter.filters.push({ field: 'identifier', operator: 'eq', value: this.patientMPI, logic: 'and' });
      }
    }
    else if (this.fhirService.patientIdContext != null)
    {
      state.filter.filters.push({ field: 'id', operator: 'eq', value: this.fhirService.patientIdContext });
    }
    else if (this.patientName)
    {
      state.filter.filters.push({ field: 'fullName', operator: 'eq', value: this.patientName });
      if (this.patientDOB)
      {
        state.filter.filters.push({ field: 'dateOfBirth', operator: 'eq', value: this.patientDOB });
      }
    }
    else
    {
      this.loaded = true;
      return;
    }

    this.memberListService.queryByState(state, (patient: any) =>
    {
      if (patient !== null && patient.data !== null)
      {
        patient.data.forEach(v =>
        {
          if (v.dateOfBirth !== null && v.dateOfBirth !== undefined && v.dateOfBirth.length === 10)
          {
            v.dateOfBirth = new Date(`${v.dateOfBirth}T12:00:00.000`);
          }
        });

        if (patient.total === 1)
        {
          this.member = patient.data[0];
          this.fhirService.saveChannelsPatient(this.member);
          this.patientContext = this.fhirService.patientContext;
        }
        else if (patient.total > 1)
        {
          this.multiplePatientsFound = true;    // Could eventually be extended to add a selector.
        }
        this.getCountMessages();
        this.loaded = true;
      }
    });
  }

  private refreshCases(delayTime: number): void
  {
    setTimeout(() =>
    {
      this.messengerService.sendMessage(new MessageSubject('reloadCaseListDetailComponent'));
    }, delayTime);
  }
}
